
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';

interface ErrorBoundaryProps {
  children?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Global Error Boundary to catch crashes and allow reload
// Fix: Use React.Component explicitly to ensure proper inheritance and property access for TS
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    // Fix: state property exists on React.Component
    this.state = { hasError: false, error: null };
  }

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Suncoast MD App Error:", error, errorInfo);
  }

  public render() {
    // Fix: state is properly recognized as a property of ErrorBoundary through React.Component
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '40px', 
          fontFamily: 'Inter, sans-serif', 
          textAlign: 'center', 
          color: '#27201A',
          backgroundColor: '#F4F5F8',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
             width: '60px', 
             height: '60px', 
             backgroundColor: '#D9924D', 
             borderRadius: '50%', 
             display: 'flex', 
             alignItems: 'center', 
             justifyContent: 'center',
             marginBottom: '20px',
             color: 'white',
             fontSize: '30px',
             fontWeight: 'bold'
          }}>!</div>
          <h1 style={{color: '#2F6B77', fontSize: '2rem', marginBottom: '1rem'}}>Something went wrong</h1>
          <p style={{maxWidth: '600px', marginBottom: '20px', lineHeight: '1.6', color: '#568490'}}>
            The application encountered an unexpected error. Please try refreshing the page.
          </p>
          <pre style={{
            background: '#ffffff', 
            padding: '20px', 
            borderRadius: '8px', 
            textAlign: 'left', 
            maxWidth: '800px', 
            width: '100%',
            overflow: 'auto',
            fontSize: '12px',
            border: '1px solid #e5e7eb',
            color: '#ef4444',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Fix: error is accessed from the component's state */}
            {this.state.error?.toString()}
          </pre>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              marginTop: '30px',
              padding: '12px 24px',
              backgroundColor: '#2F6B77',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '16px',
              boxShadow: '0 4px 6px -1px rgba(47, 107, 119, 0.3)',
              transition: 'transform 0.1s'
            }}
          >
            Reload Application
          </button>
        </div>
      );
    }
    // Fix: Access props correctly from the inherited React.Component class
    return this.props.children;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = createRoot(rootElement);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
